import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorState,
  Input,
  Label,
  LoadingSkeleton,
  ModuleIcon,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  toast,
} from '@berky/ui'
import { ArrowDown, ArrowUp, Camera, Plus, Save } from 'lucide-react'
import { useState, type FormEvent } from 'react'

import {
  feedbackIconOptions,
  useFeedbackSettings,
  useSaveFeedbackSettings,
  type FeedbackCategory,
  type FeedbackSettings,
} from '../feedback/feedback-settings-api'

export function FeedbackSettingsPage() {
  const query = useFeedbackSettings()
  const save = useSaveFeedbackSettings()
  const [draft, setDraft] = useState<FeedbackSettings | null>(null)
  const settings = draft ?? query.data

  if (query.isPending) return <LoadingSkeleton variant="form" rows={5} />
  if (query.isError || !settings) {
    return (
      <ErrorState
        description="Die Feedback-Einstellungen konnten nicht geladen werden."
        onRetry={() => void query.refetch()}
      />
    )
  }
  const current = settings

  function updateCategory(id: string, changes: Partial<FeedbackCategory>) {
    setDraft({
      ...current,
      categories: current.categories.map((category) =>
        category.id === id ? { ...category, ...changes } : category,
      ),
    })
  }

  function move(index: number, direction: -1 | 1) {
    const categories = [...current.categories]
    const other = categories[index + direction]
    const category = categories[index]
    if (!category || !other) return
    categories[index] = other
    categories[index + direction] = category
    setDraft({ ...current, categories })
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    try {
      await save.mutateAsync(current)
      setDraft(null)
      toast.success('Feedback-Einstellungen gespeichert')
    } catch (error) {
      toast.error('Die Einstellungen konnten nicht gespeichert werden', {
        description:
          error instanceof Error ? error.message : 'Bitte versuchen Sie es erneut.',
      })
    }
  }

  return (
    <form onSubmit={(event) => void submit(event)}>
      <PageHeader
        title="Feedback"
        description="Bestimmen Sie die Aktionen im Feedback-Menü. Diese Einstellungen gelten für alle Nutzer."
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              disabled={save.isPending || !draft}
              onClick={() => setDraft(null)}
            >
              Änderungen verwerfen
            </Button>
            <Button type="submit" disabled={save.isPending || !draft}>
              <Save aria-hidden="true" />
              {save.isPending ? 'Speichert …' : 'Änderungen speichern'}
            </Button>
          </>
        }
      />
      <fieldset disabled={save.isPending} className="min-w-0 space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Standardaktionen</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="flex items-center justify-between gap-4">
              <Label htmlFor="general-idea-enabled">Allgemeine Idee anzeigen</Label>
              <Switch
                id="general-idea-enabled"
                checked={settings.generalIdeaEnabled}
                onCheckedChange={(generalIdeaEnabled) =>
                  setDraft({ ...settings, generalIdeaEnabled })
                }
              />
            </div>
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Camera className="size-4 shrink-0" aria-hidden="true" />
              Feedback zu diesem Modul bleibt mit automatischer Screenshotaufnahme
              verfügbar.
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Zusätzliche Feedback-Aktionen</CardTitle>
            <p className="text-sm text-muted-foreground">
              Jede Aktion öffnet ein Fenster für Text und einen optionalen Dateianhang
              ohne automatische Screenshotaufnahme. Der Name ist zugleich die Kategorie in
              der Feedbackübersicht. Ausgeblendete Kategorien bleiben dort erhalten.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {settings.categories.map((category, index) => (
              <div
                key={category.id}
                className="grid items-end gap-4 rounded-lg border p-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_auto]"
              >
                <div className="space-y-2">
                  <Label htmlFor={`category-name-${category.id}`}>
                    Name und Kategorie
                  </Label>
                  <Input
                    id={`category-name-${category.id}`}
                    value={category.name}
                    required
                    maxLength={80}
                    onChange={(event) =>
                      updateCategory(category.id, { name: event.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`category-icon-${category.id}`}>Piktogramm</Label>
                  <Select
                    value={category.icon}
                    onValueChange={(icon) => updateCategory(category.id, { icon })}
                  >
                    <SelectTrigger id={`category-icon-${category.id}`} className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {feedbackIconOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          <span className="flex items-center gap-2">
                            <ModuleIcon name={option.value} className="size-4" />
                            {option.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex h-9 items-center gap-2">
                  <Switch
                    id={`category-enabled-${category.id}`}
                    checked={category.enabled}
                    onCheckedChange={(enabled) =>
                      updateCategory(category.id, { enabled })
                    }
                  />
                  <Label htmlFor={`category-enabled-${category.id}`}>Anzeigen</Label>
                </div>
                <div className="flex gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                    aria-label={`${category.name || 'Neue Aktion'} nach oben`}
                  >
                    <ArrowUp aria-hidden="true" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    disabled={index === settings.categories.length - 1}
                    onClick={() => move(index, 1)}
                    aria-label={`${category.name || 'Neue Aktion'} nach unten`}
                  >
                    <ArrowDown aria-hidden="true" />
                  </Button>
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              disabled={settings.categories.length >= 30}
              onClick={() =>
                setDraft({
                  ...settings,
                  categories: [
                    ...settings.categories,
                    {
                      id: crypto.randomUUID(),
                      name: '',
                      icon: 'FileText',
                      enabled: true,
                    },
                  ],
                })
              }
            >
              <Plus aria-hidden="true" />
              Feedback-Aktion hinzufügen
            </Button>
          </CardContent>
        </Card>
      </fieldset>
    </form>
  )
}
